import { SelectChip, SelectChipGroup } from '@lastbrand/web-ui/select-chip'
import { cx } from '@lastbrand/web-ui-styled-system/css'
import React from 'react'

import ProductOptionValue from '@src/quince-site-common/src/domains/ProductOption'

import { optionWrapperClass } from './option-selector-ds.styles'
import { chipCss, chipGroupCss, outOfStockChipCss } from './text-chip-group-ds.styles'

export interface TextChipGroupDSItem {
  optionValue: ProductOptionValue
  /** Stable index used to attach the legacy `data-selector-key` to the first item. */
  index: number
}

export interface TextChipGroupDSProps {
  items: TextChipGroupDSItem[]
  selectedValue: string | undefined
  onOptionSelect: (value: string) => void
  selectionSetId: string
  /** Forwarded to the per-option wrapper for legacy analytics parity. */
  selectorKey?: string
  /** Group name (e.g. "Size") for the toggle group aria-label. */
  ariaLabel?: string
  lastChanceSizes?: (string | undefined)[]
  optionWrapperClassName?: string
}

/**
 * TEXT (size) branch on PDP: one library `SelectChipGroup` Root wraps all values —
 * STRFRNT-16454 (the hand-rolled radio+label chips didn't use the DS SelectChip).
 * The Root is `display: contents` so the caller's option row keeps owning layout/gaps
 * and the `+N` overflow flows inline, exactly like the previous per-option map path.
 */
export const TextChipGroupDS = ({
  items,
  selectedValue,
  onOptionSelect,
  selectionSetId,
  selectorKey,
  ariaLabel,
  lastChanceSizes,
  optionWrapperClassName,
}: TextChipGroupDSProps) => {
  return (
    <SelectChipGroup
      type="single"
      // Radix ToggleGroup 'single' deselects on re-click (fires with '') — a product
      // property must always keep a selection, so empty values are swallowed.
      value={selectedValue ?? ''}
      onValueChange={value => {
        if (value) onOptionSelect(value)
      }}
      aria-label={ariaLabel}
      data-selection-set={selectionSetId}
      css={chipGroupCss}
    >
      {items.map(({ optionValue, index }) => (
        <div
          key={optionValue.value}
          data-content-type="option-wrapper"
          data-dd-action-name="product option"
          data-value={optionValue.value}
          data-is-selected={optionValue.value === selectedValue}
          data-selector-key={index === 0 ? selectorKey : undefined}
          className={cx(optionWrapperClass, optionWrapperClassName)}
        >
          <SelectChip
            value={optionValue.value}
            variant="rectangle"
            // Preserves the `option-container_<value>` e2e locator from the radio path.
            data-testid={`option-container_${optionValue.value}`}
            css={lastChanceSizes?.includes(optionValue.value) ? outOfStockChipCss : chipCss}
          >
            {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
            {optionValue.displayName || optionValue.value}
          </SelectChip>
        </div>
      ))}
    </SelectChipGroup>
  )
}
